import { createSelector } from '@reduxjs/toolkit';
import {
    selectMarketSelectionSchema,
    selectMarketDefaultLine,
} from '~SpectateStore/selectors/marketsSelectors';
import { selectionsAdapter } from '~SpectateStore/slices/selectionsSlice';
import {
    selectMarketIdsforUiMarket,
    selectLinesforUiMarket,
    selectColumnsForUiMarket,
} from '~SpectateStore/selectors/uiMarketSelectors';
import { selectIsUsFormat } from '~SpectateStore/selectors/categoriesSelectors';
import {
    sortedSelectionIds,
    gameLineMappedSelectionIds,
    goalScorerOrdering,
} from '~spa/Utils/SelectionSorting';

const {
    selectById: selectSelectionById,
    selectEntities: selectAllSelectionsEntities,
} = selectionsAdapter.getSelectors(state => state.selections);

export const selectSelectionName = (state, id) => selectSelectionById(state, id)?.name;
export const selectSelectionDecimalPrice = (state, id) => selectSelectionById(state, id)?.decimal_price;
export const selectSelectionFractionPrice = (state, id) => selectSelectionById(state, id)?.fraction_price;
export const selectSelectionAmericanPrice = (state, id) => selectSelectionById(state, id)?.american_price;
export const selectSelectionSpecialOddsValue = (state, id) => selectSelectionById(state, id)?.specialoddsvalue;
export const selectSelectionType = (state, id) => selectSelectionById(state, id)?.type;
export const selectSelectionHomeScore = (state, id) => selectSelectionById(state, id)?.ordering_params?.homeScore;
export const selectSelectionAwayScore = (state, id) => selectSelectionById(state, id)?.ordering_params?.awayScore;

export const selectSelectionsForMarket = createSelector(
    [
        selectAllSelectionsEntities,
        (state, marketId, eventId) => ({ marketId, eventId }),
    ],
    (selections, { marketId, eventId }) => (Object.values(selections) ?? [])
            .filter(({ market_id, event_id }) =>
                (Number(market_id) === Number(marketId)) && Number(event_id) === Number(eventId)),
);

export const selectSortedSelectionsIds = createSelector(
    [
        selectAllSelectionsEntities,
        (state, eventId, marketId) => ({
            eventId,
            marketId,
            selectionSchema: selectMarketSelectionSchema(state, marketId),
            isUsFormat: selectIsUsFormat(state, eventId),
        }),
    ],
    (selections, { eventId, marketId, selectionSchema, isUsFormat }) => {
        const selectionsForMarketEvent = Object.values(selections)
            .filter(({market_id, event_id}) => {
                return market_id === marketId && event_id === eventId;
            }
        );

        return sortedSelectionIds(selectionsForMarketEvent, selectionSchema, isUsFormat);
    }
);

export const selectSelectionIsBetable = (state, id) => {
    const selection = selectSelectionById(state, id);

    return Boolean(selection?.active) && Boolean(selection?.tradable);
};

export const selectMarketHasSelections = createSelector(
    [
        selectAllSelectionsEntities,
        (state, eventId, marketId) => ({
            eventId,
            marketId,
        }),
    ],
    (selections, {eventId, marketId}) => {
        return Object.values(selections)
            .some(({market_id, event_id, active}) => {
                return market_id === marketId && event_id === eventId && active;
            }
        );
    }
);

export const selectUiMarketHasSelections = createSelector(
    [
        state => state,
        selectAllSelectionsEntities,
        (state, eventId, marketId) => ({
            eventId,
            marketIds: selectMarketIdsforUiMarket(state, marketId) ?? [],
        }),
    ],
    (state, selections, {eventId, marketIds}) => {
        if (marketIds.length > 0) {
            return Object.values(selections)
                .some(({market_id, event_id, active}) => {
                    return marketIds.includes(market_id) && event_id === eventId && active;
                }
            );
        }

        return false;
    }
);

export const selectGameLineSelectionsIds = createSelector(
    [
        state => state,
        selectAllSelectionsEntities,
        (state, eventId, marketId, headers) => ({
            eventId,
            marketIds: selectMarketIdsforUiMarket(state, marketId),
            uiLines: selectLinesforUiMarket(state, marketId),
            headers,
            isUsFormat: selectIsUsFormat(state, eventId),
        }),
    ],
    (state, selections,
        { eventId, marketIds, uiLines, headers, isUsFormat }
    ) => {
        const selectionsForUiMarket = {};
        marketIds.forEach((marketId, idx) => {
            const defaultLine = selectMarketDefaultLine(state, eventId, marketId);
            const selectionSchema = selectMarketSelectionSchema(state, marketId);
            const uiMarketSelections = Object.values(selections)
                .filter(({market_id, event_id}) => {
                    return market_id === marketId && event_id === eventId;
            });
            if (uiMarketSelections.length > 0) {
                const uiSelIds = gameLineMappedSelectionIds(
                    uiMarketSelections,
                    defaultLine,
                    uiLines,
                    headers[idx],
                    selectionSchema,
                    isUsFormat
                );
                uiLines.forEach((line) => {
                    if (!selectionsForUiMarket[line]) {
                        selectionsForUiMarket[line] = {};
                    }
                    selectionsForUiMarket[line] = {
                        ...selectionsForUiMarket[line],
                        ...uiSelIds[line],
                    };
                });
            }
        });

        return selectionsForUiMarket;
    }
);

export const selectGoalscorerSelectionsIds = createSelector(
    [
        selectColumnsForUiMarket,
        (state, uiMarketId, eventId) => ({
            state,
            eventId,
        }),
    ],
    (columns, { state, eventId }) => {
        const selectionsGroupedByName = (columns ?? []).reduce((acc, { market_id: marketId }, index) => {
            selectSelectionsForMarket(state, marketId, eventId).forEach(selection => {
                const { name } = selection;

                if (!acc[name]) {
                    acc[name] = {
                        'name': name,
                        'selections': Array(columns.length).fill(null),
                    };
                }

                acc[name].selections[index] = selection;
            });

            return acc;
        }, {});

        // Returns `[...{ 'name': selectionName, 'ids': [selectionIdNext, selectionIdAnytime] } ]`
        return goalScorerOrdering(Object.values(selectionsGroupedByName));
    }
);
